// Copyright (c) 2024-2025. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { auth } from './FirebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { firestore } from './FirebaseConfig';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { fetchUserInfo } from '../utils/FirebaseUtils';
import { getSuppliers, getStockFields } from '../shared_components/utils/FirebaseUtils';
import { EVENT_NAMES } from '../utils/eventLogging/eventNames';
import { FALLBACK_LANGUAGE_CODE, setRegion } from '../i18n/i18n';
import { writeEventLog } from '../utils/eventLogging/writeEventLog';
import {
  getActiveGlobalLabelsets,
  getLabelsetProfileByClientRole,
  constructLabelsetGroups,
} from '../shared_components/utils/FirebaseLabelsetsUtils';
import { logEvent } from 'firebase/analytics';
import { analytics } from './FirebaseConfig';

export const AuthContext = createContext();

// Setting up an app-wide user authentication provider.
export const AuthContextProvider = ({ children }) => {
  const [authUser, setAuthUser] = useState({});
  const [userLoaded, setUserLoaded] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    uid: null,
    info: { languageCode: FALLBACK_LANGUAGE_CODE },
    company: { company_displayname: '', company: '', shift_time: 0 },
    device_id: null,
    filter_observation_classes: [],
    hasProductionLines: null, // Track if user has devices/production lines
    stampEnabled: null, // Track stamp access permission
  });
  const [suppliers, setSuppliers] = useState({});
  const [globalLabelsets, setGlobalLabelsets] = useState({});
  const [defaultLabelsets, setDefaultLabelsets] = useState({});
  const [globalLabelsetGroups, setGlobalLabelsetGroups] = useState({});
  const [defaultLabelsetGroups, setDefaultLabelsetGroups] = useState({});
  const [stockFields, setStockFields] = useState({});

  // Identity for the event log, held in a ref rather than read from state so `logEventCustom` stays
  // referentially stable: call sites list it in useEffect dependency arrays, which would re-fire
  // view events every time the user object changed.
  const sessionRef = useRef({ uid: null, client: null, device: null, operator: null });
  const loggedInUidRef = useRef(null);

  /**
   * Log one user action to `event_logs` and to Analytics. Identity is added here, never by the
   * caller.
   *
   * @param {string} eventName - Name from `EVENT_NAMES`.
   * @param {Object} [eventParams={}] - Event params; snake_case keys, no PII.
   * @returns {void}
   */
  const logEventCustom = useCallback((eventName, eventParams = {}) => {
    logEvent(analytics, eventName, eventParams);
    writeEventLog({ eventName, eventParams, session: sessionRef.current });
  }, []);

  /**
   * Record which shift operator is currently selected on this device. The shift subscription lives
   * in the stamp page, so it registers the operator here instead of the logger reaching for it.
   *
   * @param {string|null} operatorId - Key into `bale_feed_settings.users`, never the operator's name.
   * @returns {void}
   */
  const setEventLogOperator = useCallback((operatorId) => {
    sessionRef.current = { ...sessionRef.current, operator: operatorId ?? null };
  }, []);

  // Keep the logged identity current, then record the sign-in once per account.
  useEffect(() => {
    sessionRef.current = {
      ...sessionRef.current,
      uid: currentUser?.uid ?? null,
      client: currentUser?.company?.company ?? null,
      device: currentUser?.device_id ?? null,
    };

    // Signing out clears the marker, so signing back in counts as a new login.
    if (!currentUser?.uid) {
      loggedInUidRef.current = null;
      return;
    }

    if (currentUser.uid !== loggedInUidRef.current) {
      loggedInUidRef.current = currentUser.uid;
      logEventCustom(EVENT_NAMES.LOGIN, {});
    }
  }, [currentUser?.uid, currentUser?.company?.company, currentUser?.device_id, logEventCustom]);

  async function addTimestampToUser(user) {
    try {
      await updateDoc(doc(firestore, 'users', user), { last_login: serverTimestamp() });
    } catch (error) {
      console.warn('Could not update last_login', error);
    }
  }

  async function getUserData(user) {
    const currentUserNew = await fetchUserInfo(user.uid, user);
    currentUserNew['uid'] = user.uid;
    // The one place a missing user language gets its default; the rest of the app reads it as is.
    currentUserNew['info'] = {
      ...currentUserNew.info,
      languageCode: currentUserNew.info?.languageCode || FALLBACK_LANGUAGE_CODE,
    };
    currentUserNew['region'] = currentUserNew.company?.address?.country;
    setRegion(currentUserNew.region);
    if (currentUserNew.company) {
      // Fetch static data in parallel
      const [suppliersResult, stockFieldsResult, globalLabelsetsResult, defaultLabelsetProfileResult] =
        await Promise.all([
          getSuppliers(firestore, currentUserNew.company.company),
          getStockFields(firestore, currentUserNew.company.company),
          getActiveGlobalLabelsets(firestore),
          getLabelsetProfileByClientRole(firestore, currentUserNew.company, 'default'),
        ]);

      // Construct labelset groups
      const defaultLabelsetsResult = defaultLabelsetProfileResult?.labelsets || {};
      const [globalLabelsetGroupsResult, defaultLabelsetGroupsResult] = await Promise.all([
        constructLabelsetGroups(firestore, globalLabelsetsResult || {}),
        constructLabelsetGroups(firestore, defaultLabelsetsResult || {}),
      ]);

      setSuppliers(suppliersResult);
      setGlobalLabelsets(globalLabelsetsResult);
      setDefaultLabelsets(defaultLabelsetsResult);
      setGlobalLabelsetGroups(globalLabelsetGroupsResult);
      setDefaultLabelsetGroups(defaultLabelsetGroupsResult);
      setStockFields(stockFieldsResult);
    }
    setCurrentUser(currentUserNew);
    setUserLoaded(true);
  }

  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      setAuthUser(user);
      setUserLoaded(false);
      if (user) {
        getUserData(user);
        addTimestampToUser(user.uid);
      } else {
        setRegion(undefined);
        setCurrentUser({});
      }
    });
  }, []);

  return (
    // Any component wrapped into "authcontext provider" will be able to reach the current user.
    // Import into index file to cover the whole application.
    <AuthContext.Provider
      value={{
        authUser,
        currentUser,
        userLoaded,
        globalLabelsets,
        defaultLabelsets,
        globalLabelsetGroups,
        defaultLabelsetGroups,
        suppliers,
        stockFields,
        logEventCustom,
        setEventLogOperator,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
